import { Action, createAction, handleActions } from 'redux-actions'
import { call, put, select, takeLatest } from 'redux-saga/effects'
import { oc } from 'ts-optchain'

import * as Bridge from '@/api/BridgeApi'
import { checkAvailableDelivery } from '@/api/DebitCardApi'
import { fetchProfile } from '@/api/ProfileApi'
import { OKSign, STATUS_CODE } from '@/domains/CommonDomain'
import { EmptyShippingAddress } from '@/domains/DebitCardDomain'
import { AddressType, IAddress, IUserProfile } from '@/domains/ProfileDomain'
import { IRootState } from '@/modules'
import { errorActions } from '@/modules/common/error'
import { loadingActions } from '@/modules/common/loading'
import { debitCardIssueActions, IDebitCardIssueView } from '@/modules/debit-card/DebitCardIssueModule'
import { profileAsync } from '@/modules/profile/ProfileModule'
import { newError } from '@/utils/errorUtils'
import { apiSuccessReducer } from '@/utils/reducerUtils'

import { debitCardIssueSelector } from './DebitCardIssueModule'

export const DEBIT_CARD_ISSUE_SHIPPING = 'DebitCardIssueShipping'

export interface IAddressWithShippingStatus extends IAddress {
  isDisabledShipping: boolean
}

export interface IShippingAddress {
  key: AddressType
  value: IAddressWithShippingStatus
  xltCode: string
}

export interface IDebitCardIssueShippingState {
  homeAddress: IAddressWithShippingStatus
  bizAddress: IAddressWithShippingStatus
  customAddress: IAddressWithShippingStatus
}

export const initialState = {
  homeAddress: EmptyShippingAddress,
  bizAddress: EmptyShippingAddress,
  customAddress: EmptyShippingAddress,
}

const INITIALIZE_ADDRESSES = `${DEBIT_CARD_ISSUE_SHIPPING}/initializeAddresses`
const SET_ADDRESSES = `${DEBIT_CARD_ISSUE_SHIPPING}/setAddresses`
const SET_CUSTOM_ADDRESS = `${DEBIT_CARD_ISSUE_SHIPPING}/setCustomAddress`
const SEND_ADDRESS = `${DEBIT_CARD_ISSUE_SHIPPING}/sendAddress`

export const debitCardIssueShippingActions = {
  initializeAddresses: createAction(INITIALIZE_ADDRESSES),
  setAddresses: createAction(SET_ADDRESSES),
  setCustomAddress: createAction<IAddressWithShippingStatus>(SET_CUSTOM_ADDRESS),
  sendAddress: createAction(SEND_ADDRESS),
}

export const reducer = {
  [SET_ADDRESSES]: apiSuccessReducer,
  [SET_CUSTOM_ADDRESS]: (
    state: IDebitCardIssueShippingState,
    action: Action<IAddressWithShippingStatus>,
  ): IDebitCardIssueShippingState => ({
    ...state,
    customAddress: action.payload,
  }),
}

export const debitCardIssueShippingSelector = {
  addresses: (state: IRootState): IShippingAddress[] => {
    const homeAddress = oc(state[DEBIT_CARD_ISSUE_SHIPPING]).homeAddress(EmptyShippingAddress)
    const bizAddress = oc(state[DEBIT_CARD_ISSUE_SHIPPING]).bizAddress(EmptyShippingAddress)
    const home = {
      key: AddressType.HOME,
      value: homeAddress,
      xltCode: 'debitcard_shipaddress_home_desc',
    }
    const biz = {
      key: AddressType.WORK,
      value: bizAddress,
      xltCode: 'debitcard_shipaddress_company_desc',
    }

    return Object.values({ home, biz }).filter(({ value }) => isValidAddress(value))
  },
  customAddress: (state: IRootState) =>
    oc(state[DEBIT_CARD_ISSUE_SHIPPING]).customAddress(EmptyShippingAddress),
  selectedAddress: (state: IRootState) => {
    const { selectedAddress } = debitCardIssueSelector.view(state)

    return {
      [AddressType.HOME]: oc(state[DEBIT_CARD_ISSUE_SHIPPING]).homeAddress(EmptyShippingAddress),
      [AddressType.WORK]: oc(state[DEBIT_CARD_ISSUE_SHIPPING]).bizAddress(EmptyShippingAddress),
      [AddressType.ETC]: oc(state[DEBIT_CARD_ISSUE_SHIPPING]).customAddress(EmptyShippingAddress),
    }[selectedAddress]
  },
  isDisableNextButton: (state: IRootState): boolean => {
    const selectedAddress = debitCardIssueShippingSelector.selectedAddress(state)

    return !isValidAddress(selectedAddress)
  },
}


export function* sendAddress(): any {
  try {
    yield put(loadingActions.start(DEBIT_CARD_ISSUE_SHIPPING))
    const selectedAddressDetails: IAddress = yield select(debitCardIssueShippingSelector.selectedAddress)
    const { selectedAddress }: IDebitCardIssueView = yield select(debitCardIssueSelector.view)
    const data = { address: selectedAddressDetails, addressType: selectedAddress }
    yield call(Bridge.sendData, { data })
  } catch (e) {
    if (e.code === STATUS_CODE.CANNOT_DELIVERY) {
      throw newError(STATUS_CODE.CANNOT_DELIVERY)
    } else {
      yield put(errorActions.set(e))
    }
  } finally {
    yield put(loadingActions.finish(DEBIT_CARD_ISSUE_SHIPPING))
  }
}

export function* initializeAddresses(): any {
  try {
    const { isEdit, isEZL } = yield select(debitCardIssueSelector.mode)

    if (isEdit) {
      return
    }

    yield put(loadingActions.start(DEBIT_CARD_ISSUE_SHIPPING))

    const { homeAddress, bizAddress, custNm }: IUserProfile = yield call(fetchProfile, {
      isEZL: isEZL ? OKSign.YES : OKSign.NO,
    })

    if (isEZL) {
      yield put(profileAsync.success({ custNm }))
    }

    /* 직업 특성 상 bizAddress가 없는 경우가 있어 해당 주소가 있는 경우에만 주소유효성검사 진행*/
    /*const [isDisableHome, isDisableBiz] = yield all([
      call(checkAvailableDelivery, {
        cityCode: homeAddress.cityCode,
        provinceCode: homeAddress.provinceCode,
      }),
      call(checkAvailableDelivery, {
        cityCode: bizAddress.cityCode,
        provinceCode: bizAddress.provinceCode,
      }),
    ])

    yield put(
      debitCardIssueShippingActions.setAddresses({
        homeAddress: {
          ...homeAddress,
          isDisabledShipping: isDisableHome,
        },
        bizAddress: {
          ...bizAddress,
          isDisabledShipping: isDisableBiz,
        },
      }),
    )*/
    let isDisableHome, isDisableBiz
    if (homeAddress) {
      isDisableHome = yield call(checkAvailableDelivery, {
        cityCode: homeAddress.cityCode,
        provinceCode: homeAddress.provinceCode
      })
      yield put(debitCardIssueShippingActions.setAddresses({
        homeAddress: {
          ...homeAddress,
          isDisabledShipping: isDisableHome,
        }
      }))
    }
    if (bizAddress) {
      isDisableBiz = yield call(checkAvailableDelivery, {
        cityCode: bizAddress.cityCode,
        provinceCode: bizAddress.provinceCode
      })
      yield put(debitCardIssueShippingActions.setAddresses({
        bizAddress: {
          ...bizAddress,
          isDisabledShipping: isDisableBiz,
        }
      }))
    }

    if (isDisableHome && isDisableBiz) {
      yield put(
        debitCardIssueActions.setView({
          selectedAddress: AddressType.ETC,
        }),
      )
    } else if (isDisableHome) {
      yield put(
        debitCardIssueActions.setView({
          selectedAddress: AddressType.WORK,
        }),
      )
    }
  } catch (e) {
    if (e.code === STATUS_CODE.CANNOT_DELIVERY) {
      throw newError(STATUS_CODE.CANNOT_DELIVERY)
    } else {
      yield put(errorActions.set(e))
    }
  } finally {
    yield put(loadingActions.finish(DEBIT_CARD_ISSUE_SHIPPING))
  }
}

export const debitCardIssueShippingReducer = handleActions(reducer, initialState)
export const debitCardIssueShippingSaga = [takeLatest(INITIALIZE_ADDRESSES, initializeAddresses), takeLatest(SEND_ADDRESS, sendAddress)]

function isValidAddress(address: IAddressWithShippingStatus): boolean {
  return address.zipCode !== ''
}
